'use client';

import { useTranslations } from 'next-intl';
import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { buildSubmitErrorMessage, buildYoloSegmentContent } from '../_utils';
import { submitMessageTasks, updateMessage } from '../apis';
import type { ChatMessageItem } from '../types';

type ErrorLike = { reason?: string; message?: string } | null | undefined;

const toErrorLike = (err: unknown): ErrorLike =>
  err && typeof err === 'object' && 'reason' in err
    ? (err as ErrorLike)
    : { message: err instanceof Error ? err.message : undefined };

export const useStoryActions = ({
  messages,
  setMessages,
  activeSessionId,
  sessionMediaFolderMap,
}: {
  messages: ChatMessageItem[];
  setMessages: Dispatch<SetStateAction<ChatMessageItem[]>>;
  activeSessionId: string | null | undefined;
  sessionMediaFolderMap: Record<string, string>;
}) => {
  const t = useTranslations();

  // 分镜详情弹窗：用来源消息 id 控制开关，找不到对应消息即视为关闭。
  const [storyDialogMessageId, setStoryDialogMessageId] = useState<string | null>(null);
  // 正在提交分镜任务的消息 id 集合：UpdateMessage / SubmitMessageTasks 期间给按钮上 loading。
  const [submittingStoryMessageIds, setSubmittingStoryMessageIds] = useState<Set<string>>(() => new Set());

  const storyDialogMessage = useMemo(
    () => messages.find((m) => m.id === storyDialogMessageId && m.story),
    [messages, storyDialogMessageId],
  );

  const markStorySubmitting = useCallback((messageId: string, submitting: boolean) => {
    setSubmittingStoryMessageIds((prev) => {
      const next = new Set(prev);
      if (submitting) next.add(messageId);
      else next.delete(messageId);
      return next;
    });
  }, []);

  /**
   * 把后端 _Error.reason 映射成对用户更友好的多语言提示。
   * - 路径相关的提示（如 `ClawSessionMaterialNotFound`）用 {folder} 注入当前 session 的 media 目录；
   * - 未知 reason 落到 `submitError.default`，避免直接把后端英文 message 抛给用户。
   */
  const explainSubmitError = useCallback(
    (error: ErrorLike): string => {
      const expectedFolder = activeSessionId
        ? sessionMediaFolderMap[activeSessionId] || `media/sess-${activeSessionId}`
        : 'media/sess-<sessionId>';
      return buildSubmitErrorMessage(error, { t, expectedFolder });
    },
    [t, activeSessionId, sessionMediaFolderMap],
  );

  const handleOpenStoryDetail = useCallback((messageId: string) => {
    setStoryDialogMessageId(messageId);
  }, []);

  const handleSubmitStory = useCallback(
    async (messageId: string) => {
      const targetMessage = messages.find((m) => m.id === messageId);
      if (!targetMessage?.story) return { ok: false, error: 'story not found' } as const;
      const serverMessageId = targetMessage.serverMessageId;
      if (!serverMessageId) {
        toast({ type: 'error', message: t('claw.storyDetail.chat.messageNotPersistedSubmit') });
        return { ok: false, error: 'missing server message id' } as const;
      }
      markStorySubmitting(messageId, true);
      try {
        const reply = await submitMessageTasks(serverMessageId);
        // hey-api client 不抛错：HTTP 4xx/5xx 时 reply.error 有值、reply.data 为空。
        if (reply.error) {
          const message = explainSubmitError(reply.error as ErrorLike);
          toast({ type: 'error', message });
          return { ok: false, error: message, suppressToast: true } as const;
        }
        const results = reply.data?.results;
        const allSucceeded =
          Array.isArray(results) && results.length > 0 && results.every((item) => item.success === true);
        if (allSucceeded) {
          toast({ type: 'success', message: t('claw.storyDetail.panel.submitSuccess') });
          return { ok: true, suppressToast: true } as const;
        }
        const errMessage = reply.data?.message || t('claw.chat.submitError.default');
        toast({ type: 'error', message: errMessage });
        return { ok: false, error: errMessage, suppressToast: true } as const;
      } catch (err) {
        // 网络异常或非业务错误：交给 explainSubmitError 走多语言兜底。
        const message = explainSubmitError(toErrorLike(err));
        toast({ type: 'error', message });
        return { ok: false, error: message, suppressToast: true } as const;
      } finally {
        markStorySubmitting(messageId, false);
      }
    },
    [messages, markStorySubmitting, explainSubmitError, t],
  );

  const handleSubmitStoryFromDialog = useCallback(
    async (payload: NonNullable<ChatMessageItem['story']>) => {
      if (!storyDialogMessageId) return { ok: false, error: 'no message' } as const;
      const targetMessage = messages.find((m) => m.id === storyDialogMessageId);
      const serverMessageId = targetMessage?.serverMessageId;
      if (!serverMessageId) {
        toast({ type: 'error', message: t('claw.storyDetail.chat.messageNotPersistedSave') });
        return { ok: false, error: 'missing server message id' } as const;
      }
      // 把弹窗里的 draft 通过 UpdateMessage 落回后端，再触发 SubmitMessageTasks。
      // updateMessage 也是耗时请求，提前点亮 submitting 让按钮立刻有反馈。
      markStorySubmitting(storyDialogMessageId, true);
      try {
        const updateReply = await updateMessage(serverMessageId, [buildYoloSegmentContent(payload)]);
        if (updateReply.error) {
          const message = explainSubmitError(updateReply.error as ErrorLike);
          toast({ type: 'error', message });
          markStorySubmitting(storyDialogMessageId, false);
          return { ok: false, error: message, suppressToast: true } as const;
        }
      } catch (err) {
        const message = explainSubmitError(toErrorLike(err));
        toast({ type: 'error', message });
        markStorySubmitting(storyDialogMessageId, false);
        return { ok: false, error: message, suppressToast: true } as const;
      }
      setMessages((prev) => prev.map((m) => (m.id === storyDialogMessageId ? { ...m, story: payload } : m)));
      // handleSubmitStory 内部会再 mark 一次 true，结束后由它的 finally 收尾。
      return handleSubmitStory(storyDialogMessageId);
    },
    [storyDialogMessageId, messages, handleSubmitStory, setMessages, markStorySubmitting, explainSubmitError, t],
  );

  return {
    storyDialogMessageId,
    setStoryDialogMessageId,
    storyDialogMessage,
    submittingStoryMessageIds,
    handleOpenStoryDetail,
    handleSubmitStory,
    handleSubmitStoryFromDialog,
  };
};
